import React, { useState } from "react";
import { Input, Select, SelectItem } from "@heroui/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { Plus, Trash } from "@phosphor-icons/react";
import { CompanyDirector } from "shared/types/yally-company";
import { Contact } from "shared/types/contact";
import { ContactSearchSelect } from "screens/buildings/components/renovationTab/modals/components/ContactSearchSelect";
import CompanySearchInput from "./CompanySearchInput";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DirectorType = "persoon" | "bedrijf";

interface CompanyDirectorListProps {
  directors: CompanyDirector[];
  onChange: (directors: CompanyDirector[]) => void;
  isDisabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_DIRECTOR: CompanyDirector = {
  contact: undefined,
  contactCompany: undefined,
  role: "",
};

function resolveType(director: CompanyDirector): DirectorType {
  if (director.contactCompany) return "bedrijf";
  return "persoon";
}

// ---------------------------------------------------------------------------
// DirectorRow
// ---------------------------------------------------------------------------

interface DirectorRowProps {
  director: CompanyDirector;
  index: number;
  isDisabled: boolean;
  onFieldChange: (index: number, patch: Partial<CompanyDirector>) => void;
  onRemove: (index: number) => void;
}

const DirectorRow: React.FC<DirectorRowProps> = ({
  director,
  index,
  isDisabled,
  onFieldChange,
  onRemove,
}) => {
  const [type, setType] = useState<DirectorType>(resolveType(director));

  const handleTypeChange = (newType: DirectorType) => {
    setType(newType);
    onFieldChange(index, {
      contact: undefined,
      contactCompany: undefined,
      representative: undefined,
    });
  };

  const handleContactChange = (contact: Contact | null) => {
    if (!contact) {
      onFieldChange(index, { contact: undefined });
      return;
    }
    onFieldChange(index, {
      contact: {
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
      },
      contactCompany: undefined,
    });
  };

  const handleContactCompanyChange = (
    company: { id: number; name: string } | null,
  ) => {
    if (!company) {
      onFieldChange(index, { contactCompany: undefined });
      return;
    }
    onFieldChange(index, {
      contactCompany: { id: company.id, name: company.name },
      contact: undefined,
    });
  };

  const handleRepresentativeChange = (contact: Contact | null) => {
    if (!contact) {
      onFieldChange(index, { representative: undefined });
      return;
    }
    onFieldChange(index, {
      representative: {
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
      },
    });
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(index, { role: e.target.value });
  };

  const representativeValue: Contact | null = director.representative
    ? ({
        id: director.representative.id,
        firstName: director.representative.firstName,
        lastName: director.representative.lastName,
      } as Contact)
    : null;

  const contactValue: Contact | null = director.contact
    ? ({
        id: director.contact.id,
        firstName: director.contact.firstName,
        lastName: director.contact.lastName,
      } as Contact)
    : null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-3">
      <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
        {/* Type selector */}
        <Select
          label="Type"
          selectedKeys={new Set([type])}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as DirectorType;
            if (selected) handleTypeChange(selected);
          }}
          size="sm"
          variant="bordered"
          isDisabled={isDisabled}
          classNames={{ label: "text-sm font-medium", value: "text-sm" }}
        >
          <SelectItem key="persoon">Persoon</SelectItem>
          <SelectItem key="bedrijf">Bedrijf</SelectItem>
        </Select>

        {/* Contact or company picker */}
        <div className="flex flex-col justify-end">
          {type === "persoon" ? (
            <ContactSearchSelect
              value={contactValue}
              onChange={handleContactChange}
              placeholder="Zoek contactpersoon..."
            />
          ) : (
            <CompanySearchInput
              value={director.contactCompany ?? null}
              onChange={handleContactCompanyChange}
              isDisabled={isDisabled}
            />
          )}
        </div>

        {/* Role */}
        <Input
          label="Rol"
          value={director.role ?? ""}
          onChange={handleRoleChange}
          placeholder="bijv. Voorzitter, Zaakvoerder"
          size="sm"
          variant="bordered"
          isDisabled={isDisabled}
          classNames={{ label: "text-sm font-medium", input: "text-sm" }}
        />

        {/* Representative — only when director is a company */}
        {type === "bedrijf" && (
          <div className="col-span-1 flex flex-col gap-1.5 sm:col-span-3">
            <p className="text-sm font-medium text-neutral-secondary-mid">
              Vaste vertegenwoordiger
            </p>
            <ContactSearchSelect
              value={representativeValue}
              onChange={handleRepresentativeChange}
              placeholder="Zoek vertegenwoordiger..."
            />
          </div>
        )}
      </div>

      {!isDisabled && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="mt-2 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
          aria-label="Bestuurder verwijderen"
        >
          <Trash size={16} />
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CompanyDirectorList — main export
// ---------------------------------------------------------------------------

const CompanyDirectorList: React.FC<CompanyDirectorListProps> = ({
  directors,
  onChange,
  isDisabled = false,
}) => {
  const handleAdd = () => {
    onChange([...directors, { ...EMPTY_DIRECTOR }]);
  };

  const handleRemove = (index: number) => {
    onChange(directors.filter((_, i) => i !== index));
  };

  const handleFieldChange = (
    index: number,
    patch: Partial<CompanyDirector>,
  ) => {
    const updated = directors.map((item, i) =>
      i === index ? { ...item, ...patch } : item,
    );
    onChange(updated);
  };

  return (
    <div className="flex flex-col gap-3">
      {directors.length === 0 && (
        <p className="text-sm text-gray-400">Geen bestuurders toegevoegd</p>
      )}

      {directors.map((director, index) => (
        <DirectorRow
          key={director.id ?? `new-${index}`}
          director={director}
          index={index}
          isDisabled={isDisabled}
          onFieldChange={handleFieldChange}
          onRemove={handleRemove}
        />
      ))}

      {!isDisabled && (
        <ButtonNew
          variant="secondaryMoss"
          onClick={handleAdd}
          icon={<Plus />}
          label="Bestuurder toevoegen"
        />
      )}
    </div>
  );
};

export default CompanyDirectorList;
